/**
 * The candle chart's time-axis labels, in the mode the rest of the interface is using.
 *
 * ## 🔴 Why this exists: the axis was the one clock nobody set
 *
 * `CandleChart`'s own comment states the risk exactly and then does not prevent it:
 *
 * > Everything is UTC (H5). The library would otherwise label the axis in the browser's zone, and
 * > `AppHeader` says UTC in the corner — two clocks on one screen is how a trade log gets misread.
 *
 * The options it sets under that comment are `timeVisible` and `secondsVisible`, and **neither
 * controls the timezone.** `TimeScaleOptions.tickMarkFormatter` defaults to `undefined`, so the
 * library's built-in formatter runs — the one the comment says labels in the browser's zone. So the
 * axis has been rendering in an unconfigured zone underneath a caption asserting `times in UTC`.
 *
 * ⚠ **The fix is not "force UTC".** `UI-Design-Spec.md` line 120 wants a mode, so the axis follows
 * the same switch as every other timestamp — and setting the formatter at all is what makes the
 * answer *defined* rather than inherited from a library default nobody chose.
 *
 * ## The 8-character budget is the library's, not a preference
 *
 * `TickMarkFormatter`'s contract: *"the returned string should be the shortest possible value and
 * should have no more than 8 characters. Otherwise, the tick marks will overlap each other."* That
 * is why the labels here are terse and why the granularity comes from `tickMarkType` rather than
 * from one format for everything.
 */

import type { TickMarkType, Time } from 'lightweight-charts';

import type { TimestampMode } from '../ui/timestamps';

/** Month names at three characters, which is what fits the budget. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * A `Time` from the library as a `Date`.
 *
 * ⚠ **`UTCTimestamp` is in SECONDS**, which the library's own typings warn about: *"JavaScript Date
 * APIs like `Date.now` return a number of milliseconds but UTCTimestamp expects a number of
 * seconds."* Multiplying is the whole conversion, and getting it wrong puts every label in 1970.
 *
 * The other two `Time` shapes are a business-day object and an ISO string. This chart only ever
 * supplies seconds, but the type admits all three and a formatter that threw on one would take the
 * axis down rather than mislabel it.
 */
function dateOf(time: Time): Date | null {
  if (typeof time === 'number') return new Date(time * 1000);
  if (typeof time === 'string') {
    const parsed = new Date(time);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof time === 'object' && time !== null && 'year' in time) {
    return new Date(Date.UTC(time.year, time.month - 1, time.day));
  }
  return null;
}

/**
 * One axis label, at the granularity the library asked for and in the requested mode.
 *
 * Returns `null` where the time cannot be read, which the library documents as *"the default tick
 * mark formatter will be used as a fallback"* — a wrong-looking label beats a blank axis.
 */
export function axisTickLabel(
  time: Time,
  tickMarkType: TickMarkType,
  mode: TimestampMode,
): string | null {
  const at = dateOf(time);
  if (at === null) return null;
  const utc = mode === 'utc';
  const year = utc ? at.getUTCFullYear() : at.getFullYear();
  const month = utc ? at.getUTCMonth() : at.getMonth();
  const day = utc ? at.getUTCDate() : at.getDate();
  const hour = utc ? at.getUTCHours() : at.getHours();
  const minute = utc ? at.getUTCMinutes() : at.getMinutes();
  const second = utc ? at.getUTCSeconds() : at.getSeconds();

  // The numeric values of `TickMarkType` — Year 0, Month 1, DayOfMonth 2, Time 3, TimeWithSeconds 4.
  // Compared numerically rather than against the enum so this module does not need a runtime import
  // from the library, which would pull the chart bundle into a unit test.
  switch (tickMarkType as unknown as number) {
    case 0:
      return String(year);
    case 1:
      return MONTHS[month] ?? String(month + 1);
    case 2:
      // `Aug 07` rather than `07`: a bare day is ambiguous the moment the range crosses a month,
      // and it still fits the eight characters.
      return `${MONTHS[month] ?? ''} ${pad(day)}`.trim();
    case 4:
      return `${pad(hour)}:${pad(minute)}:${pad(second)}`;
    default:
      return `${pad(hour)}:${pad(minute)}`;
  }
}

/**
 * The crosshair's own label, which is a different budget and a different question.
 *
 * The tick marks answer *"where am I on the axis"* and have eight characters; the crosshair answers
 * *"exactly which bar is this"* and has the whole tooltip, so it carries the date as well as the
 * clock. Both must agree about the mode or the chart contradicts itself under the cursor.
 */
export function crosshairTimeLabel(time: Time, mode: TimestampMode): string {
  const at = dateOf(time);
  if (at === null) return '';
  const utc = mode === 'utc';
  const year = utc ? at.getUTCFullYear() : at.getFullYear();
  const month = utc ? at.getUTCMonth() : at.getMonth();
  const day = utc ? at.getUTCDate() : at.getDate();
  const hour = utc ? at.getUTCHours() : at.getHours();
  const minute = utc ? at.getUTCMinutes() : at.getMinutes();
  return `${year}-${pad(month + 1)}-${pad(day)} ${pad(hour)}:${pad(minute)}`;
}
